import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { APIError, api } from "@/lib/api";

// Types
export interface TeamMember {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: string;
  status?: "active" | "pending";
}

interface UpdateRoleVariables {
  memberId: string;
  role: string;
}

interface UpdateRoleContext {
  previousMembers: TeamMember[] | undefined;
}

interface InviteTeamMemberVariables {
  email: string;
  role: string;
}

// API response type
interface ApiResponse<T> {
  data: T;
}

// Constants
const QUERY_KEYS = {
  team: ["company", "team"] as const,
} as const;

/**
 * Hook for fetching team members
 * @returns Query result containing team members data, loading state, and error
 */
export function useTeam() {
  return useQuery<TeamMember[]>({
    queryKey: QUERY_KEYS.team,
    queryFn: () => api.get<TeamMember[]>("company/team/"),
    retry: false,
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook for updating a team member's role
 * Includes optimistic updates and automatic error handling
 */
export function useUpdateRole() {
  const queryClient = useQueryClient();

  return useMutation<TeamMember, Error, UpdateRoleVariables, UpdateRoleContext>(
    {
      mutationFn: async ({ memberId, role }) => {
        const { data } = await api.put<ApiResponse<TeamMember>>(
          `company/team/${memberId}/role/`,
          { role },
        );
        return data;
      },

      // Optimistically update the UI before the mutation completes
      onMutate: async ({ memberId, role }) => {
        // Cancel any outgoing refetches to avoid overwriting our optimistic update
        await queryClient.cancelQueries({ queryKey: QUERY_KEYS.team });

        // Snapshot the current value
        const previousMembers = queryClient.getQueryData<TeamMember[]>(
          QUERY_KEYS.team,
        );

        // Optimistically update the team member's role
        if (previousMembers) {
          queryClient.setQueryData<TeamMember[]>(
            QUERY_KEYS.team,
            previousMembers.map((member) =>
              member.id === memberId ? { ...member, role } : member,
            ),
          );
        }

        // Return context for rollback on error
        return { previousMembers };
      },
      onError: (error, _variables, context) => {
        if (context?.previousMembers) {
          queryClient.setQueryData(QUERY_KEYS.team, context.previousMembers);
        }

        toast.error("Error updating role", {
          description: error.message,
        });
      },
      onSuccess: (_data, variables) => {
        toast("Role updated", {
          description: `Successfully updated team member's role to ${variables.role}.`,
        });
      },

      // After success or error, refetch to ensure we're in sync with the server
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.team });
      },
    },
  );
}

/**
 * Hook for inviting new team members
 * Includes automatic error handling and success notifications
 */
export function useInviteTeamMember() {
  const queryClient = useQueryClient();

  return useMutation<
    ApiResponse<{ message: string }>,
    Error,
    InviteTeamMemberVariables
  >({
    mutationFn: async ({ email, role }) => {
      return api.post<ApiResponse<{ message: string }>>(
        "company/team/invite/",
        {
          email,
          role,
        },
      );
    },
    onSuccess: (_data, { email }) => {
      toast("Invitation sent", {
        description: `An invitation has been sent to ${email}`,
      });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.team });
    },
    onError: (error) => {
      // Check if this is an email_already_invited error
      if (
        error instanceof APIError &&
        error.data?.error_code === "email_already_invited"
      ) {
        toast.warning("Email already invited", {
          description:
            "This email has already been invited to the organization",
        });
        return;
      }

      // Generic error handling for other cases
      toast.error("Error sending invitation", {
        description: error.message,
      });
    },
  });
}

interface RemoveTeamMemberContext {
  previousMembers: TeamMember[] | undefined;
}

interface RemoveTeamMemberVariables {
  memberId: string;
  isPending?: boolean;
}

/**
 * Hook for removing team members or pending invitations
 * Includes optimistic updates and automatic error handling
 */
export function useRemoveTeamMember() {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    RemoveTeamMemberVariables,
    RemoveTeamMemberContext
  >({
    mutationFn: async ({ memberId }) => {
      await api.delete(`company/team/${memberId}/`);
    },

    // Optimistically update the UI before the mutation completes
    onMutate: async ({ memberId, isPending }) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.team });

      // Snapshot the current value
      const previousMembers = queryClient.getQueryData<TeamMember[]>(
        QUERY_KEYS.team,
      );

      // Optimistically remove the team member
      if (previousMembers) {
        queryClient.setQueryData<TeamMember[]>(
          QUERY_KEYS.team,
          previousMembers.filter((member) =>
            isPending ? member.email !== memberId : member.id !== memberId,
          ),
        );
      }

      // Return context for rollback on error
      return { previousMembers };
    },

    onError: (error, _variables, context) => {
      // Rollback to the previous state on error
      if (context?.previousMembers) {
        queryClient.setQueryData(QUERY_KEYS.team, context.previousMembers);
      }

      toast.error("Error removing team member", {
        description: error.message,
      });
    },

    onSuccess: (_data, { isPending }) => {
      toast(isPending ? "Invitation cancelled" : "Team member removed", {
        description: isPending
          ? "Successfully cancelled the invitation"
          : "Successfully removed team member from the company",
      });
    },
  });
}
